import React, {Fragment, useEffect, useState} from 'react'
import {Link, useLocation, useNavigate, useParams, useSearchParams} from 'react-router-dom'
import api from '../../utils/Api'
import {IncomeDocumentStateTypes, IncomeDocumentTypes} from '../../constants/Common'
import Tab from 'react-bootstrap/Tab'
import Tabs from 'react-bootstrap/Tabs'
import TabDocumentDetail from './income-document-detail/TabDocumentDetail'
import StateLabel from '../grid/StateLabel'
import TabDocumentPayment from './income-document-detail/TabDocumentPayment'
import TabDocumentCredit from './income-document-detail/TabDocumentCredit'
import TabDocumentDebit from './income-document-detail/TabDocumentDebit'
import IncomeDocumentStepper from './income-document-detail/IncomeDocumentStepper'
import TabDocumentBilling from './income-document-detail/TabDocumentBilling'
import TabDocumentDeposit from './income-document-detail/TabDocumentDeposit'
import {getRevenuePrefixPath} from '../../utils/Helper'
import {Button, Col, Row} from 'react-bootstrap'
import TabDocumentPurchaseNote from './income-document-detail/TabDocumentPurchaseNote'
import TabDocumentDepositExpense from './income-document-detail/TabDocumentDepositExpense'
import TabDocumentExpensePurchaseNote from './income-document-detail/TabDocumentExpensePurchaseNote'
import {ConfirmModal} from '../Modals'
import TabDocumentAccountBook, {
  GetAccountBook,
} from './income-document-detail/TabDocumentAccountBook'
import TabDocumentExpenseAccountBook from './income-document-detail/TabDocumentExpenseAccountBook'
import ModalOutstandingPayment from '../modal/ModalOutstandingPayment'
import {useAuth} from '../../modules/auth'

export interface IIncomeDocument {
  id: number
  actionBy: any
  actionById: number
  approver: any
  approverId: number
  approvalDate: string
  branch: any
  branchId: number
  code: string
  taxCode: string
  invoiceNo?: string | null
  taxExpense: string
  createdAt: string
  date: string
  invoiceDate: string
  purchaseTaxDate: string
  department: any
  departmentId: number | null
  discountPrice: number
  expenseCode: string
  expiryDate: string
  filePpThirty: number
  billingDocuments: any[]
  billingHistories: any[]
  childrenDocuments: any[]
  childrenDocumentsJson?: any[]
  deductionItems?: any[]
  incomeDocumentItems: any[]
  incomeDocumentPayments: any[]
  incomeDocumentPaymentFees: any[]
  incomeDocumentPaymentDeductions: any[]
  incomeDocumentDepositDeductions: any[]
  incomeDocumentDepositDeductionAppliers: any[]
  incomeDocumentDebt: any
  issueTaxInvoice: number
  labCompany: any
  labCompanyId: number
  linkingDocument: any
  linkingDocumentId: number
  netAmount: number
  note: string | null
  saleChannel: string
  seller: any
  sellerId: number
  conditionDay?: number
  state: string
  totalAmount: number
  type: IncomeDocumentTypes
  updatedAt: string
  vatPrice: number
  vatType: string
  whTax: number | null
  whTaxPrice: number
}

const IncomeDocumentDetailPage = () => {
  const location = useLocation()
  const {id} = useParams()
  const navigate = useNavigate()
  const [document, setDocument] = useState<IIncomeDocument | undefined>(undefined)
  const [currentTab, setCurrentTab] = useState<string | null>('profile')
  const [accountBook, setAccountBook] = useState<GetAccountBook | null>(null)
  const [searchParams] = useSearchParams()
  const tab = searchParams.get('tab')
  const isExpense = document && document?.type.includes('expense')

  const [cancelModal, setCancelModal] = useState(false)
  const [paymentModal, setPaymentModal] = useState(false)
  const {can} = useAuth()

  const getAccountBook = (code?: string) => {
    if (!code) return

    api.request(`account/income-document/${code}/account-book`, (r, s) => {
      if (!s) return

      if (r) {
        setAccountBook(r)
      }
    })
  }

  useEffect(() => {
    api.request(`account/income-document/${id}`, (res) => {
      // console.log(124214201442102140, res)
      setDocument(res)
      getAccountBook(res?.code)
    })
  }, [])

  useEffect(() => {
    if (!tab) return

    setCurrentTab(tab)
  }, [])

  const pathArray = location.pathname.slice(1).split('/')
  const mainPath = pathArray[0]

  if (!document) return <></>

  const hasIssueTaxInvoice = document.issueTaxInvoice
  const creditDocuments = document.childrenDocuments?.filter(
    (doc) => doc.type === IncomeDocumentTypes.CREDIT
  )
  const debitDocuments = document.childrenDocuments?.filter(
    (doc) => doc.type === IncomeDocumentTypes.DEBIT
  )
  const isInExpensePage = location.pathname.includes('expense')
  const isInExpenseRequestPurchaseOrder =
    document.type === IncomeDocumentTypes.EXPENSE_REQUEST_PURCHASE_ORDER
  const isInvoice = document.type === IncomeDocumentTypes.INVOICE

  // รับชำระได้เฉพาะใบที่รอชำระหรือเกินกำหนด ให้ตรงกับเมนู "รับชำระ" ในหน้ารายการ
  // stepper มองทุก state ที่ไม่ใช่ paid เป็น pending ถ้าไม่กันตรงนี้จะเปิดทางรับชำระ
  // ใบที่ยังเป็นร่าง ยังไม่อนุมัติ หรือยกเลิกไปแล้ว
  const canReceiveInvoicePayment =
    isInvoice &&
    [IncomeDocumentStateTypes.OUTSTANDING, IncomeDocumentStateTypes.OVERDUE].includes(
      document.state as IncomeDocumentStateTypes
    ) &&
    can('accounting_invoice_edit')

  const openPaymentModal = () => {
    // ฝัง ?tab=payment ไว้ใน URL ก่อนเปิด modal เพราะ ModalOutstandingPayment ยิง navigate(0)
    // เองในเคสชำระบางส่วน เราจึงแทรกอะไรระหว่างนั้นไม่ได้ ใช้ replaceState แทน setSearchParams
    // เพื่อไม่ให้แท็บสลับให้เห็นขณะ modal ยังเปิดอยู่ แล้วปล่อยให้การรีโหลดอ่านค่าเองตอน mount
    const url = new URL(window.location.href)
    url.searchParams.set('tab', 'payment')
    window.history.replaceState(null, '', url.toString())
    setPaymentModal(true)
  }

  const shouldRenderPaymentTab = [
    IncomeDocumentTypes.DEPOSIT,
    IncomeDocumentTypes.INVOICE,
    IncomeDocumentTypes.RECEIPT,
    IncomeDocumentTypes.DEBIT,
    IncomeDocumentTypes.CREDIT,
    IncomeDocumentTypes.BILLING,
    IncomeDocumentTypes.EXPENSE_DEPOSIT_PAYMENT,
    IncomeDocumentTypes.EXPENSE_COMBINED_PAYMENT_NOTE,
    IncomeDocumentTypes.EXPENSE_PURCHASE_INVENTORY_RECORD,
    IncomeDocumentTypes.EXPENSE_EXPENSE_RECORD,
    IncomeDocumentTypes.EXPENSE_PURCHASE_CREDIT_NOTE,
    IncomeDocumentTypes.EXPENSE_PURCHASE_DEBIT_NOTE,
  ].includes(document.type)
  const shouldRenderDepositTab = [
    IncomeDocumentTypes.DEPOSIT,
    IncomeDocumentTypes.EXPENSE_DEPOSIT_PAYMENT,
  ].includes(document.type)
  const shouldRenderDepositExpenseTab = [
    IncomeDocumentTypes.EXPENSE_PURCHASE_INVENTORY_RECORD,
    IncomeDocumentTypes.EXPENSE_EXPENSE_RECORD,
  ].includes(document.type)
  const shouldRenderCreditDebitNoteTab = [
    IncomeDocumentTypes.EXPENSE_PURCHASE_CREDIT_NOTE,
    IncomeDocumentTypes.EXPENSE_PURCHASE_DEBIT_NOTE,
  ].includes(document.type)
  const hasPaymentAccountBooks = document.incomeDocumentPayments?.some(
    (p: any) => p?.accountBooks?.length > 0
  )

  const getTitleLabel = () => {
    switch (document.type) {
      case IncomeDocumentTypes.QUOTATION:
        return `ใบเสนอราคา ${hasIssueTaxInvoice ? '/ ใบกำกับภาษี' : ''}`
      case IncomeDocumentTypes.DEPOSIT:
        return `ใบรับเงินมัดจำ ${hasIssueTaxInvoice ? '/ ใบกำกับภาษี' : ''}`
      case IncomeDocumentTypes.INVOICE:
        return `ใบแจ้งหนี้ ${hasIssueTaxInvoice ? '/ ใบกำกับภาษี / ใบส่งสินค้า' : ''}`
      case IncomeDocumentTypes.RECEIPT:
        return `ใบเสร็จรับเงิน ${hasIssueTaxInvoice ? '/ ใบกำกับภาษี / ใบส่งสินค้า' : ''}`
      case IncomeDocumentTypes.TAX_INVOICE:
        return `ใบกำกับภาษีขาย`
      case IncomeDocumentTypes.CREDIT:
        return `ใบลดหนี้ ${hasIssueTaxInvoice ? '/ ใบกำกับภาษี' : ''}`
      case IncomeDocumentTypes.DEBIT:
        return `ใบเพิ่มหนี้ ${hasIssueTaxInvoice ? '/ ใบกำกับภาษี' : ''}`
      case IncomeDocumentTypes.BILLING:
        return `ใบวางบิล ${hasIssueTaxInvoice ? '/ ใบกำกับภาษี' : ''}`
      case IncomeDocumentTypes.EXPENSE_REQUEST_PURCHASE_ORDER:
        return 'ใบขอสั่งซื้อ'
      case IncomeDocumentTypes.EXPENSE_PURCHASE_ORDER:
        return 'ใบสั่งซื้อ'
      case IncomeDocumentTypes.EXPENSE_DEPOSIT_PAYMENT:
        return 'ใบจ่ายเงินมัดจำ'

      case IncomeDocumentTypes.EXPENSE_PURCHASE_INVENTORY_RECORD:
        return 'บันทึกซื้อสินค้า'
      case IncomeDocumentTypes.EXPENSE_EXPENSE_RECORD:
        return 'บันทึกค่าใช้จ่าย'
      case IncomeDocumentTypes.EXPENSE_PURCHASE_TAX_INVOICE:
        return 'ใบกำกับภาษีซื้อ'
      case IncomeDocumentTypes.EXPENSE_PURCHASE_CREDIT_NOTE:
        return 'รับใบลดหนี้'
      case IncomeDocumentTypes.EXPENSE_PURCHASE_DEBIT_NOTE:
        return 'รับใบเพิ่มหนี้'
      case IncomeDocumentTypes.EXPENSE_COMBINED_PAYMENT_NOTE:
        return 'ใบรวมจ่าย'
      default:
        return ''
    }
  }

  const notShowLinkingDocument = !(
    shouldRenderDepositExpenseTab &&
    (document?.linkingDocument?.type === 'expense-purchase-debit-note' ||
      document?.linkingDocument?.type === 'expense-purchase-credit-note')
  )

  const depositDeductionAppliers = document?.incomeDocumentDepositDeductionAppliers.filter(
    (item) => item.isDraft === 0
  )

  const dnData = document?.incomeDocumentPaymentDeductions?.filter(
    (item) => item?.code?.includes('DN') && !item?.isUseForPaid
  )
  const dbnxData = document?.incomeDocumentPaymentDeductions?.filter(
    (item) => item?.code?.includes('DBNX') && !item?.isUseForPaid
  )

  const dnDocumentData = {
    ...document,
    incomeDocumentPaymentDeductions: dnData,
  }
  const dbnxDocumentData = {
    ...document,
    incomeDocumentPaymentDeductions: dbnxData,
  }

  return (
    <div>
      <IncomeDocumentStepper
        document={document}
        onReceivePayment={canReceiveInvoicePayment ? openPaymentModal : undefined}
      />
      {canReceiveInvoicePayment && (
        <ModalOutstandingPayment
          document={document}
          show={paymentModal}
          onClose={() => setPaymentModal(false)}
          onSubmitSuccess={() => {
            setPaymentModal(false)
            // เคสชำระครบ modal ส่งกลับมาที่นี่ ส่วนเคสชำระบางส่วน modal รีโหลดเอง
            // ทั้งสองทางกลับมาที่ URL เดิมที่ถือ ?tab=payment อยู่แล้ว จึงเห็นรายการที่เพิ่งบันทึกทันที
            navigate(0)
          }}
        />
      )}
      <div className='d-flex flex-column gap-3 flex-wrap'>
        <div
          className='row-vcenter cursor-pointer link fs-5'
          onClick={() => {
            navigate(-1)
          }}
        >
          <i className='bi bi-arrow-left fs-6 me-3'></i>ย้อนกลับ
        </div>
        <h1 className={'mb-0'}>{`${getTitleLabel()} เลขที่ #${document?.code}`}</h1>
      </div>
      <div className={'d-flex justify-content-between'}>
        <div className={'flex-1 d-flex flex-column justify-content-center fs-6'}>
          <div className={'d-flex flex-1 align-items-center'}>
            <span className={'me-1'}>{'สถานะเอกสาร :'}</span>
            <strong>
              <StateLabel state={document.state} />
            </strong>
          </div>
          {document?.taxCode && (
            <div className={'d-flex flex-1 align-items-center'}>
              <span className={'me-1'}>{'ใบกำกับภาษี :'}</span>
              <strong>{document?.taxCode}</strong>
            </div>
          )}
          {isInExpenseRequestPurchaseOrder && (
            <div className={'flex-1 d-flex align-items-center fs-6'}>
              <span className={'me-1'}>{'หน่วยงานที่ขอซื้อ :'}</span>
              {(document?.departmentId || document?.branchId) && (
                <strong>{document?.department?.name || document?.branch?.name}</strong>
              )}
            </div>
          )}
        </div>
        <div>
          {/* ใบแจ้งหนี้รับชำระได้จากปุ่มใน stepper แล้ว ปุ่มนี้จึงซ้ำซ้อน แต่เอกสารประเภทอื่น
              ปุ่ม "รับชำระเงิน" ใน stepper ยังไม่มี handler จึงต้องคงทางนี้ไว้ให้ */}
          {!isInvoice &&
            !!mainPath &&
            !!document?.id &&
            [IncomeDocumentStateTypes.OUTSTANDING, IncomeDocumentStateTypes.OVERDUE].includes(
              document?.state as IncomeDocumentStateTypes
            ) && (
              <Link
                style={{marginRight: '6px'}}
                className={'btn btn-info px-2 py-1'}
                to={`/${mainPath}/${document?.state}`}
              >
                {'ไปที่หน้าชำระ'}
              </Link>
            )}

          {!!id &&
            !!mainPath &&
            !!document?.id &&
            ([IncomeDocumentTypes.EXPENSE_PURCHASE_CREDIT_NOTE].includes(
              document?.type as IncomeDocumentTypes
            ) ||
              [IncomeDocumentTypes.EXPENSE_PURCHASE_DEBIT_NOTE].includes(
                document?.type as IncomeDocumentTypes
              )) &&
            [IncomeDocumentStateTypes.OUTSTANDING].includes(
              document?.state as IncomeDocumentStateTypes
            ) && (
              <Link
                style={{marginRight: '6px', padding: '6px'}}
                className={'btn btn-outline-info px-2 py-1'}
                to={`/${mainPath}/${document?.id}/edit`}
              >
                {'แก้ไข'}
              </Link>
            )}

          {!!id &&
            !!mainPath &&
            !!document?.id &&
            ([IncomeDocumentTypes.EXPENSE_PURCHASE_CREDIT_NOTE].includes(
              document?.type as IncomeDocumentTypes
            ) ||
              [IncomeDocumentTypes.EXPENSE_PURCHASE_DEBIT_NOTE].includes(
                document?.type as IncomeDocumentTypes
              )) &&
            [IncomeDocumentStateTypes.OUTSTANDING].includes(
              document?.state as IncomeDocumentStateTypes
            ) && (
              <>
                <button
                  style={{marginRight: '6px'}}
                  onClick={() => setCancelModal(true)}
                  className={'btn btn-danger px-2 py-1'}
                >
                  {'ยกเลิก'}
                </button>

                <ConfirmModal
                  show={cancelModal}
                  title={`ยกเลิก ${getTitleLabel()} เลขที่ #${document?.code}`}
                  content={''}
                  confirmText='ยืนยัน'
                  onHide={() => setCancelModal(false)}
                  onConfirm={() => {
                    api.request(
                      `account/income-document/${id}`,
                      (r, s) => {
                        setCancelModal(false)
                        window.location.reload()
                      },
                      api.form({state: IncomeDocumentStateTypes.VOIDED}, null),
                      'PATCH',
                      {showSuccess: true}
                    )
                  }}
                />
              </>
            )}

          {!!mainPath && !!document?.id && document?.state === 'draft' && (
            <Link
              style={{marginRight: '6px'}}
              className={'btn btn-info px-2 py-1'}
              to={`/${mainPath}/${document?.id}/edit`}
            >
              {'แก้ไข'}
            </Link>
          )}
          <Link
            className={'btn btn-primary px-2 py-1'}
            to={`${getRevenuePrefixPath(document?.type)}/${document?.id}/print`}
            target={'_blank'}
          >
            {'พิมพ์'}
          </Link>
          <div className={'d-none'}>{/*<PrintRevenue currentRef={printRef} />*/}</div>
        </div>
      </div>
      {notShowLinkingDocument && (document?.linkingDocument || document?.childrenDocumentsJson) && (
        <div className='d-flex flex-column fs-6'>
          {isInExpensePage && document?.type === IncomeDocumentTypes.EXPENSE_PURCHASE_ORDER ? (
            <>
              {document?.linkingDocument && (
                <div className='d-flex align-items-center flex-wrap'>
                  <span className='me-1'>{'อ้างอิงจากระบบ :'}</span>
                  <Link
                    to={`${getRevenuePrefixPath(document.linkingDocument.type)}/${document.linkingDocument.id}/detail`}
                    className='fw-bold'
                  >
                    {document.linkingDocument.code}
                  </Link>
                </div>
              )}
              {!!document?.childrenDocumentsJson?.length && (
                <div className='d-flex align-items-center flex-wrap'>
                  <span className='me-1'>{'บันทึกซื้อสินค้า :'}</span>
                  {document.childrenDocumentsJson.map((doc, index, array) => (
                    <React.Fragment key={doc.id}>
                      <Link
                        to={`${getRevenuePrefixPath(doc.type)}/${doc.id}/detail`}
                        className='fw-bold'
                      >
                        {doc.code}
                      </Link>
                      {index < array.length - 1 && <span className='me-1'>,</span>}
                    </React.Fragment>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className='d-flex align-items-center flex-wrap'>
              {!![document?.linkingDocument, ...(document?.childrenDocumentsJson || [])].filter(Boolean).length && (
                <span className='me-1'>{'อ้างอิงจากระบบ :'}</span>
              )}
              {[document?.linkingDocument, ...(document?.childrenDocumentsJson || [])]
                .filter(Boolean)
                .map((doc, index, array) => (
                  <React.Fragment key={doc.id}>
                    <Link
                      to={`${getRevenuePrefixPath(doc.type)}/${doc.id}/detail`}
                      className='fw-bold'
                    >
                      {doc.code}
                    </Link>
                    {index < array.length - 1 && <span className='me-1'>,</span>}
                  </React.Fragment>
                ))}
            </div>
          )}
        </div>
      )}
      <Tabs
        activeKey={currentTab || 'profile'}
        onSelect={(k) => setCurrentTab(k)}
        className={'mt-3 px-3'}
      >
        <Tab
          title={'ข้อมูลเอกสาร'}
          eventKey={'profile'}
          tabClassName={`fs-5 fw-bold ${
            currentTab === 'profile' ? 'text-white bg-primary px-3' : 'bg-secondary'
          }`}
        >
          <TabDocumentDetail document={document} />
        </Tab>
        {shouldRenderPaymentTab && (
          <Tab
            title={'ข้อมูลการชำระ'}
            eventKey={'payment'}
            tabClassName={`fs-5 fw-bold ${
              currentTab === 'payment' ? 'text-white bg-primary px-3' : 'bg-secondary'
            }`}
          >
            <TabDocumentPayment
              document={document}
              creditDocuments={creditDocuments}
              onSetCurrentTab={(tab: string) => {
                setCurrentTab(tab)
              }}
            />
          </Tab>
        )}
        {shouldRenderDepositTab &&
          (!!depositDeductionAppliers?.length ||
            !!document?.incomeDocumentDepositDeductions?.length ||
            !!creditDocuments?.length) && (
            <Tab
              title={'ข้อมูลการหักเงินมัดจำ'}
              eventKey={'deposit'}
              tabClassName={`fs-5 fw-bold ${
                currentTab === 'deposit' ? 'text-white bg-primary px-3' : 'bg-secondary'
              }`}
            >
              <TabDocumentDeposit document={document} creditDocuments={creditDocuments} />
            </Tab>
          )}
        {shouldRenderDepositExpenseTab &&
          !!document?.incomeDocumentDepositDeductions?.length &&
          document?.state !== IncomeDocumentStateTypes.DRAFT && (
            <Tab
              title={'ข้อมูลการหักเงินมัดจำ'}
              eventKey={'deposit'}
              tabClassName={`fs-5 fw-bold ${
                currentTab === 'deposit' ? 'text-white bg-primary px-3' : 'bg-secondary'
              }`}
            >
              <TabDocumentDepositExpense document={document} />
            </Tab>
          )}

        {shouldRenderDepositExpenseTab &&
          ((!!document?.incomeDocumentPaymentDeductions?.length && !!dnData && !!dnData.length) ||
            document?.linkingDocument?.code.startsWith('DN')) && (
            <Tab
              title={'รับข้อมูลใบลดหนี้'}
              eventKey={'expense-credit-debit'}
              tabClassName={`fs-5 fw-bold ${
                currentTab === 'expense-credit-debit'
                  ? 'text-white bg-primary px-3'
                  : 'bg-secondary'
              }`}
            >
              <TabDocumentExpensePurchaseNote
                data={dnDocumentData}
                type={IncomeDocumentTypes.EXPENSE_PURCHASE_CREDIT_NOTE}
              />
            </Tab>
          )}

        {shouldRenderDepositExpenseTab &&
          ((!!document?.incomeDocumentPaymentDeductions?.length &&
            !!dbnxData &&
            !!dbnxData.length) ||
            document?.linkingDocument?.code.startsWith('DBNX')) && (
            <Tab
              title={'รับข้อมูลใบเพิ่มหนี้'}
              eventKey={'expense-debit-debit'}
              tabClassName={`fs-5 fw-bold ${
                currentTab === 'expense-debit-debit' ? 'text-white bg-primary px-3' : 'bg-secondary'
              }`}
            >
              <TabDocumentExpensePurchaseNote
                data={dbnxDocumentData}
                type={IncomeDocumentTypes.EXPENSE_PURCHASE_DEBIT_NOTE}
              />
            </Tab>
          )}

        {/* {shouldRenderCreditDebitNoteTab &&
          !!document?.incomeDocumentDepositDeductionAppliers?.length && (
            <Tab
              title={
                document?.type === IncomeDocumentTypes.EXPENSE_PURCHASE_CREDIT_NOTE
                  ? 'ข้อมูลการตัดใบลดหนี้'
                  : 'ข้อมูลการตัดใบเพิ่มหนี้'
              }
              eventKey={'purchase-note'}
              tabClassName={`fs-5 fw-bold ${
                currentTab === 'purchase-note' ? 'text-white bg-primary px-3' : 'bg-secondary'
              }`}
            >
              <TabDocumentPurchaseNote data={document} />
            </Tab>
          )} */}
        {creditDocuments?.length > 0 && (
          <Tab
            title={'ใบลดหนี้'}
            eventKey={'credit'}
            tabClassName={`fs-5 fw-bold ${
              currentTab === 'credit' ? 'text-white bg-primary px-3' : 'bg-secondary'
            }`}
          >
            <TabDocumentCredit document={document} creditDocuments={creditDocuments} />
          </Tab>
        )}
        {debitDocuments?.length > 0 && (
          <Tab
            title={'ใบเพิ่มหนี้'}
            eventKey={'debit'}
            tabClassName={`fs-5 fw-bold ${
              currentTab === 'debit' ? 'text-white bg-primary px-3' : 'bg-secondary'
            }`}
          >
            <TabDocumentDebit document={document} debitDocuments={debitDocuments} />
          </Tab>
        )}
        {document?.billingHistories?.length > 0 && (
          <Tab
            title={isExpense ? 'ข้อมูลใบรวมจ่าย' : 'ข้อมูลใบวางบิล'}
            eventKey={'billingNote'}
            tabClassName={`fs-5 fw-bold ${
              currentTab === 'billingNote' ? 'text-white bg-primary px-3' : 'bg-secondary'
            }`}
          >
            <TabDocumentBilling document={document} />
          </Tab>
        )}
        {!!accountBook && (
          <Tab
            title='สมุดบัญชี'
            eventKey={'accountBook'}
            tabClassName={`fs-5 fw-bold ${
              currentTab === 'accountBook' ? 'text-white bg-primary px-3' : 'bg-secondary'
            }`}
          >
            <TabDocumentAccountBook accountBook={accountBook} />
          </Tab>
        )}
        {isExpense && hasPaymentAccountBooks && (
          <Tab
            title='สมุดบัญชี'
            eventKey={'expenseAccountBook'}
            tabClassName={`fs-5 fw-bold ${
              currentTab === 'expenseAccountBook' ? 'text-white bg-primary px-3' : 'bg-secondary'
            }`}
          >
            <TabDocumentExpenseAccountBook document={document} />
          </Tab>
        )}
      </Tabs>
    </div>
  )
}

export default IncomeDocumentDetailPage
